import { ref } from 'vue';
import httpService from '../services/httpService';
import { useDateFormatter } from './useDateFormatter';
import type { RelatoriosOS_ClientesRow } from '../contracts/RelatoriosOS_ClientesRow';
import type { RelatoriosOS_ServicosRow } from '../contracts/RelatoriosOS_ServicosRow';
import type { RelatoriosOS_ProdutividadeRow } from '../contracts/RelatoriosOS_ProdutividadeRow';
import type { RelatoriosOS_ConsumoRow } from '../contracts/RelatoriosOS_ConsumoRow';

/**
 * Relatórios de OS (chamado NS 2026-0084): vendas por cliente, vendas por
 * serviço e produtividade por usuário; consumo por cliente (NS 2026-0104). As
 * regras de cada número estão no Relatorios_os_model do backend.
 *
 * Uma chamada por relatório, já filtrada pelo filtro global. Sem tratamento de
 * moeda (decisão do Ivan, 27/10/2026: moedas entram na migração do legado da
 * Doutor Sofá).
 *
 * O dinheiro chega em CENTAVOS (os Row contracts e `so_orders` guardam assim)
 * e sai daqui em unidades da moeda, que é o que o `renderType: 'currency'` da
 * tabela formata — a mesma divisão por 100 que o controller de OS faz.
 */
export type TipoRelatorioOS = 'clientes' | 'servicos' | 'produtividade' | 'consumo';

export type LinhaClienteOS = Omit<RelatoriosOS_ClientesRow, 'ultima_ordem_em'> & { ultima_ordem_em: Date | null };
export type LinhaConsumoOS = Omit<RelatoriosOS_ConsumoRow, 'primeira_data' | 'ultima_data'> & { primeira_data: Date | null; ultima_data: Date | null };
export type LinhaRelatorioOS = LinhaClienteOS | RelatoriosOS_ServicosRow | RelatoriosOS_ProdutividadeRow | LinhaConsumoOS;

const URLS: Record<TipoRelatorioOS, string> = {
  clientes: '/index.php/relatorios/relatorios_os/clientes',
  servicos: '/index.php/relatorios/relatorios_os/servicos',
  produtividade: '/index.php/relatorios/relatorios_os/produtividade',
  consumo: '/index.php/relatorios/relatorios_os/consumo',
};

/** Os campos em centavos de cada relatório. */
const EM_CENTAVOS: Record<TipoRelatorioOS, string[]> = {
  clientes: ['total', 'ticket_medio'],
  servicos: ['valor', 'valor_medio'],
  produtividade: ['valor', 'ticket_medio'],
  consumo: ['valor', 'ticket_medio', 'valor_por_par'],
};

const CONTRATOS: Record<TipoRelatorioOS, string> = {
  clientes: 'RelatoriosOS_ClientesResponse',
  servicos: 'RelatoriosOS_ServicosResponse',
  produtividade: 'RelatoriosOS_ProdutividadeResponse',
  consumo: 'RelatoriosOS_ConsumoResponse',
};

export function useRelatoriosOS(tipo: TipoRelatorioOS) {
  const { fromApiDateTimeFormat, fromApiDateToDate } = useDateFormatter();
  const linhas = ref<LinhaRelatorioOS[]>([]);
  const carregando = ref<boolean>(false);
  const erro = ref<string | null>(null);

  async function carregar(): Promise<void> {
    carregando.value = true;
    erro.value = null;

    try {
      const response = await httpService.get(
        URLS[tipo],
        { responseContract: CONTRATOS[tipo] },
      ) as { dados?: Array<Record<string, unknown>> };

      linhas.value = (Array.isArray(response?.dados) ? response.dados : []).map((linha) => {
        const emReais = { ...linha };
        for (const campo of EM_CENTAVOS[tipo]) {
          // valor_por_par vem null quando não há pares: null não é zero.
          emReais[campo] = linha[campo] === null ? null : (Number(linha[campo]) || 0) / 100;
        }

        if (tipo === 'clientes') {
          return { ...emReais, ultima_ordem_em: fromApiDateTimeFormat((linha.ultima_ordem_em as string | null) ?? null) };
        }
        if (tipo === 'consumo') {
          return {
            ...emReais,
            primeira_data: fromApiDateToDate((linha.primeira_data as string | null) ?? null),
            ultima_data: fromApiDateToDate((linha.ultima_data as string | null) ?? null),
          };
        }
        return emReais;
      }) as LinhaRelatorioOS[];
    } catch (e) {
      erro.value = e instanceof Error ? e.message : String(e);
      linhas.value = [];
    } finally {
      carregando.value = false;
    }
  }

  return { linhas, carregando, erro, carregar };
}
